import React, { useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';

const PageSection = styled.section`
  padding: 4rem 0;
  direction: rtl;
`;

const PageContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: #374151;
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
`;

const GiftsGrid = styled.div`
  display: grid;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const GiftCard = styled.div`
  background-color: ${props => props.selected ? '#ea580c' : 'white'};
  color: ${props => props.selected ? 'white' : '#111827'};
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
  border: 2px solid ${props => props.selected ? '#ea580c' : '#ea580c'};
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px -3px rgba(0, 0, 0, 0.15);
  }
`;

const GiftBadge = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: ${props => props.selected ? 'rgba(255, 255, 255, 0.2)' : '#fed7aa'};
  color: ${props => props.selected ? 'white' : '#ea580c'};
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const GiftIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const GiftImage = styled.img`
  width: 8rem;
  height: 8rem;
  object-fit: cover;
  border-radius: 1rem;
  margin-bottom: 1.5rem;
`;

const GiftTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const GiftDescription = styled.p`
  line-height: 1.625;
  margin-bottom: 2rem;
  color: ${props => props.selected ? '#fed7aa' : '#374151'};
`;

const GiftFeatures = styled.ul`
  text-align: right;
  margin-bottom: 2rem;
  color: ${props => props.selected ? '#fed7aa' : '#374151'};
  
  li {
    margin-bottom: 0.5rem;
    position: relative;
    padding-right: 1.5rem;
    
    &:before {
      content: "✓";
      position: absolute;
      right: 0;
      color: ${props => props.selected ? 'white' : '#ea580c'};
      font-weight: bold;
    }
  }
`;

const SelectGiftButton = styled.button`
  background-color: ${props => props.selected ? 'white' : '#ea580c'};
  color: ${props => props.selected ? '#ea580c' : 'white'};
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.selected ? '#f9fafb' : '#c2410c'};
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const FormSection = styled.div`
  background-color: #f9fafb;
  padding: 3rem;
  border-radius: 0.75rem;
  margin-bottom: 3rem;
  display: ${props => props.show ? 'block' : 'none'};
`;

const FormTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;
  text-align: center;
`;

const FormSubtitle = styled.p`
  color: #374151;
  text-align: center;
  margin-bottom: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FormGroup = styled.div`
  ${props => props.fullWidth && `
    @media (min-width: 640px) {
      grid-column: span 2;
    }
  `}
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #ea580c;
  color: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #c2410c;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-left: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background-color: #d1fae5;
  border: 1px solid #10b981;
  color: #065f46;
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
  margin-top: 1rem;
`;

// Free Gifts Page Component
const FreeGiftsPage = () => {
  const [selectedGift, setSelectedGift] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Form data for personalized workout plan
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    height: '',
    weight: '',
    experience: '',
    goals: '',
    additionalInfo: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGiftSelection = (giftType) => {
    setSelectedGift(giftType);
    setIsSubmitted(false);
  };

  const handleSubmit = async () => {
    if (selectedGift === 'workout-plan') {
      if (!formData.name || !formData.email || !formData.age || !formData.height || !formData.weight || !formData.experience || !formData.goals) {
        alert('אנא מלא את כל השדות הנדרשים.');
        return;
      }
    } else if (selectedGift === 'app-access') {
      if (!formData.name || !formData.email) {
        alert('אנא מלא את השם והאימייל.');
        return;
      }
    }

    setIsLoading(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone || 'לא צוין',
        gift_type: selectedGift === 'app-access' ? 'גישה פרמיום לאפליקציה' : 'תוכנית אימון מותאמת אישית',
        age: formData.age || 'לא רלוונטי',
        height: formData.height || 'לא רלוונטי',
        weight: formData.weight || 'לא רלוונטי',
        experience: formData.experience || 'לא רלוונטי',
        goals: formData.goals || 'לא רלוונטי',
        additional_info: formData.additionalInfo || 'אין מידע נוסף',
        message: `בקשה למתנה בחינם: ${selectedGift === 'app-access' ? 'גישה פרמיום לאפליקציה' : 'תוכנית אימון מותאמת אישית'}`,
        to_email: 'haggaimagen87@gmail.com'
      };

      await emailjs.send(
        'service_w3pkjf6',
        'template_lwf20nq',
        templateParams,
        'Sgy1Do0paUmA8QyPR'
      );

      setIsSubmitted(true);
      alert(`תודה ${formData.name}! הבקשה נשלחה בהצלחה. נחזור אליך בקרוב עם המתנה שלך.`);
    } catch (error) {
      console.error('EmailJS Error:', error);
      alert('שגיאה בשליחת הבקשה. אנא נסה שוב או צור קשר טלפונית.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageSection>
      <PageContainer>
        <PageHeader>
          <PageTitle>🎁 מתנות בחינם</PageTitle>
          <PageSubtitle>
            קבל מתנות חינם שיעזרו לך להתחיל את מסע הכושר שלך בצורה הטובה ביותר. 
            בחר את המתנה שמתאימה לך ותתחיל להשתנות כבר היום!
          </PageSubtitle>
        </PageHeader>

        <GiftsGrid>
          {/* Premium App Access Gift */}
          <GiftCard 
            selected={selectedGift === 'app-access'}
            onClick={() => handleGiftSelection('app-access')}
          >
            <GiftBadge selected={selectedGift === 'app-access'}>מתנה #1</GiftBadge>
            <GiftImage 
              src="/logo_shade_transparent.png" 
              alt="אפליקציית כושר"
            />
            <GiftTitle>מנוי פרמיום לאפליקציית האימונים Beestmode</GiftTitle>
            <GiftDescription selected={selectedGift === 'app-access'}>
              קבל גישה חינם למשך חודש שלם לאפליקציית הכושר הפרמיום שלנו עם תוכניות אימון, 
              מעקב התקדמות ותזונה מותאמת אישית.
            </GiftDescription>
            <GiftFeatures selected={selectedGift === 'app-access'}>
              <li>מעל 200 תרגילי כושר עם הדרכה וידאו</li>
              <li>תוכניות אימון מותאמות לכל הרמות</li>
              <li>מעקב התקדמות ואנליטיקס מתקדמת</li>
              <li>מדריכי תזונה וחישוב קלוריות</li>
              <li>תמיכה טכנית 24/7</li>
            </GiftFeatures>
            <SelectGiftButton 
              selected={selectedGift === 'app-access'}
              onClick={(e) => {
                e.stopPropagation();
                handleGiftSelection('app-access');
              }}
            >
              {selectedGift === 'app-access' ? 'נבחר ✓' : 'בחר מתנה זו'}
            </SelectGiftButton>
          </GiftCard>

          {/* Personalized Workout Plan Gift */}
          <GiftCard 
            selected={selectedGift === 'workout-plan'}
            onClick={() => handleGiftSelection('workout-plan')}
          >
            <GiftBadge selected={selectedGift === 'workout-plan'}>מתנה #2</GiftBadge>
            <GiftIcon>💪</GiftIcon>
            <GiftTitle>תוכנית אימון מותאמת אישית</GiftTitle>
            <GiftDescription selected={selectedGift === 'workout-plan'}>
              קבל תוכנית אימון מלאה שנבנתה במיוחד עבורך על פי הגיל, הגובה, המשקל, 
              הניסיון והמטרות שלך. תוכנית מקצועית במתנה!
            </GiftDescription>
            <GiftFeatures selected={selectedGift === 'workout-plan'}>
              <li>הערכה מקצועית של מצבך הנוכחי</li>
              <li>תוכנית אימון למשך 4 שבועות</li>
              <li>התאמה מלאה למטרות האישיות שלך</li>
              <li>הנחיות לביצוע תרגילים</li>
              <li>המלצות תזונתיות בסיסיות</li>
              <li>מעקב והתאמות לפי הצורך</li>
            </GiftFeatures>
            <SelectGiftButton 
              selected={selectedGift === 'workout-plan'}
              onClick={(e) => {
                e.stopPropagation();
                handleGiftSelection('workout-plan');
              }}
            >
              {selectedGift === 'workout-plan' ? 'נבחר ✓' : 'בחר מתנה זו'}
            </SelectGiftButton>
          </GiftCard>
        </GiftsGrid>

        {/* Form for App Access */}
        <FormSection show={selectedGift === 'app-access'}>
          <FormTitle>קבלת מנוי פרמיום לאפליקציה</FormTitle>
          <FormSubtitle>מלא את הפרטים שלך וקבל קישור להורדת האפליקציה עם מנוי פרמיום</FormSubtitle>
          
          <FormGrid>
            <FormGroup>
              <Label htmlFor="name">שם מלא *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="הזן את השם המלא שלך"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">כתובת אימייל *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="הזן את כתובת האימייל שלך"
              />
            </FormGroup>
            <FormGroup fullWidth>
              <Label htmlFor="phone">מספר טלפון</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="050-"
              />
            </FormGroup>
            <FormGroup fullWidth>
              <SubmitButton onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? (
                  <>
                    שולח בקשה...
                    <LoadingSpinner />
                  </>
                ) : (
                  'קבל גישה פרמיום'
                )}
              </SubmitButton>
            </FormGroup>
          </FormGrid>
          
          {isSubmitted && (
            <SuccessMessage>
              תודה! הבקשה נשלחה בהצלחה. תקבל קישור להורדת האפליקציה בקרוב.
            </SuccessMessage>
          )}
        </FormSection>

        {/* Form for Personalized Workout Plan */}
        <FormSection show={selectedGift === 'workout-plan'}>
          <FormTitle>בניית תוכנית אימון מותאמת אישית</FormTitle>
          <FormSubtitle>מלא את הפרטים שלך בקפידה כדי שנוכל לבנות עבורך תוכנית מותאמת בדיוק למצבך הנוכחי והמטרות שלך</FormSubtitle>
          
          <FormGrid>
            <FormGroup>
              <Label htmlFor="name">שם מלא *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="הזן את השם המלא שלך"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">כתובת אימייל *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="הזן את כתובת האימייל שלך"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="phone">מספר טלפון</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="050-"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="age">גיל *</Label>
              <Input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="הזן את הגיל שלך"
                min="16"
                max="80"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="height">גובה (ס"מ) *</Label>
              <Input
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="170"
                min="140"
                max="220"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="weight">משקל (ק"ג) *</Label>
              <Input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="70"
                min="40"
                max="200"
              />
            </FormGroup>
            <FormGroup fullWidth>
              <Label htmlFor="experience">ניסיון בחדר הכושר *</Label>
              <Select
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">בחר את רמת הניסיון שלך</option>
                <option value="beginner">מתחיל (0-6 חודשים)</option>
                <option value="intermediate">בינוני (6 חודשים - 2 שנים)</option>
                <option value="advanced">מתקדם (2+ שנים)</option>
                <option value="expert">מומחה (5+ שנים)</option>
                <option value="none">ללא ניסיון בכלל</option>
              </Select>
            </FormGroup>
            <FormGroup fullWidth>
              <Label htmlFor="goals">מטרות אימון *</Label>
              <Select
                id="goals"
                name="goals"
                value={formData.goals}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">בחר את המטרה העיקרית שלך</option>
                <option value="weight-loss">ירידה במשקל וחיטוב</option>
                <option value="muscle-gain">עלייה במסת שריר</option>
                <option value="strength">הגברת כוח וביצועים</option>
                <option value="endurance">שיפור כושר אירובי וסיבולת</option>
                <option value="general-fitness">כושר כללי ובריאות</option>
                <option value="rehabilitation">החלמה ושיקום</option>
                <option value="sports-specific">אימון ספציפי לספורט</option>
              </Select>
            </FormGroup>
            <FormGroup fullWidth>
              <Label htmlFor="additionalInfo">מידע נוסף (פציעות, מגבלות, העדפות)</Label>
              <TextArea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                disabled={isLoading}
                rows={4}
                placeholder="ספר לנו על פציעות קיימות, מגבלות גופניות, העדפות באימון או כל מידע נוסף שחשוב שנדע..."
              />
            </FormGroup>
            <FormGroup fullWidth>
              <SubmitButton onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? (
                  <>
                    יוצר תוכנית מותאמת...
                    <LoadingSpinner />
                  </>
                ) : (
                  'קבל תוכנית מותאמת אישית'
                )}
              </SubmitButton>
            </FormGroup>
          </FormGrid>
          
          {isSubmitted && (
            <SuccessMessage>
              תודה! הפרטים נשלחו בהצלחה. נתחיל לעבוד על התוכנית המותאמת שלך ונשלח אותה אליך תוך 24-48 שעות.
            </SuccessMessage>
          )}
        </FormSection>
      </PageContainer>
    </PageSection>
  );
};

export default FreeGiftsPage;